import { Ionicons } from '@expo/vector-icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, Easing, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import * as Haptics from 'expo-haptics';

import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useOnAppActive } from '@/hooks/useOnAppActive';
import { supabase } from '@/lib/supabase';
import { playTap } from '@/lib/sounds';

const STREAK_TARGET = 7;

type Status = {
  checked_in_today: boolean;
  streak: number;
  progress: number;
  best: number;
};

/**
 * Ημερήσια δήλωση παρουσίας. Κάθε 7 συνεχόμενες μέρες δίνει 1 μελάνι —
 * γι' αυτό κάθεται δίπλα στην ένδειξη μελανιού στην αρχική.
 *
 * Δύο καταστάσεις:
 *  • Δεν έχει δηλώσει σήμερα → κουμπί που αναπηδά, για να τραβήξει το βλέμμα.
 *  • Έχει δηλώσει → το γραφικό με τις μέρες του σερί.
 * Στο πάτημα παίζει animation επιτυχίας και το κουμπί δίνει τη θέση του στο
 * γραφικό, ώστε η ανταμοιβή να φαίνεται αμέσως.
 *
 * Η «μέρα» ορίζεται server-side σε ώρα Ελλάδας, ώστε το σερί να μη σπάει
 * άδικα ανάλογα με τη ζώνη ώρας της συσκευής.
 */
export function DailyCheckin({ onRewarded }: { onRewarded?: () => void }) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const [status, setStatus] = useState<Status | null>(null);
  const [busy, setBusy] = useState(false);
  const [justRewarded, setJustRewarded] = useState(false);

  // Το κουμπί αναπηδά διαρκώς μέχρι να πατηθεί.
  const bounce = useRef(new Animated.Value(0)).current;
  // Εναλλαγή κουμπί → γραφικό ημερών.
  const buttonAnim = useRef(new Animated.Value(1)).current;
  const panelAnim = useRef(new Animated.Value(0)).current;
  // Στιγμιαίο τικ επιτυχίας πάνω από το γραφικό.
  const burst = useRef(new Animated.Value(0)).current;

  const load = useCallback(async () => {
    const { data, error } = await supabase.rpc('get_checkin_status' as never);
    if (error || !data) return;
    const s = data as unknown as Status;
    setStatus(s);
    // Αν έχει ήδη δηλώσει (π.χ. επιστροφή στην οθόνη), δείξε κατευθείαν το
    // γραφικό χωρίς animation εισόδου.
    buttonAnim.setValue(s.checked_in_today ? 0 : 1);
    panelAnim.setValue(s.checked_in_today ? 1 : 0);
  }, [buttonAnim, panelAnim]);

  useEffect(() => { void load(); }, [load]);
  useOnAppActive(load);

  // Το bounce τρέχει μόνο όσο υπάρχει κάτι να πατηθεί.
  useEffect(() => {
    if (!status || status.checked_in_today) return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(bounce, { toValue: 1, duration: 420, easing: Easing.out(Easing.quad), useNativeDriver: true }),
        Animated.timing(bounce, { toValue: 0, duration: 420, easing: Easing.bounce, useNativeDriver: true }),
        Animated.delay(900),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [status, bounce]);

  async function checkIn() {
    if (busy || status?.checked_in_today) return;
    setBusy(true);
    try {
      const { data, error } = await supabase.rpc('daily_checkin' as never);
      if (error) throw error;
      const res = data as unknown as { streak: number; progress: number; rewarded: boolean };

      playTap();
      if (Platform.OS !== 'web') {
        // Πιο έντονο feedback στη μέρα που δίνει μελάνι.
        const haptic = res.rewarded
          ? Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success)
          : Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
        void haptic.catch(() => {});
      }

      setStatus({
        checked_in_today: true,
        streak: res.streak,
        progress: res.progress,
        best: Math.max(status?.best ?? 0, res.streak),
      });
      if (res.rewarded) {
        setJustRewarded(true);
        onRewarded?.();
      }

      // Το κουμπί συρρικνώνεται και σβήνει, το γραφικό μπαίνει από κάτω,
      // και ένα τικ «σκάει» για μια στιγμή πάνω του.
      Animated.sequence([
        Animated.timing(buttonAnim, { toValue: 0, duration: 180, easing: Easing.in(Easing.quad), useNativeDriver: true }),
        Animated.parallel([
          Animated.spring(panelAnim, { toValue: 1, friction: 6, tension: 80, useNativeDriver: true }),
          Animated.sequence([
            Animated.spring(burst, { toValue: 1, friction: 4, tension: 120, useNativeDriver: true }),
            Animated.delay(res.rewarded ? 1100 : 550),
            Animated.timing(burst, { toValue: 0, duration: 220, useNativeDriver: true }),
          ]),
        ]),
      ]).start();
    } catch {
      // Σιωπηλή αποτυχία: η δήλωση παρουσίας δεν είναι κρίσιμη ενέργεια και
      // δεν θέλουμε modal σφάλματος στην αρχική. Θα ξαναπροσπαθήσει αύριο.
    } finally {
      setBusy(false);
    }
  }

  if (!status) return null;

  const done = status.checked_in_today;
  // Στην 7η μέρα το progress γυρνάει σε 0 — δείχνουμε 7/7 γεμάτο, όχι 0/7.
  const filled = done && status.progress === 0 && status.streak > 0 ? STREAK_TARGET : status.progress;

  return (
    <View style={styles.wrap}>
      {/* ── Κουμπί (πριν τη δήλωση) ── */}
      {!done && (
        <Animated.View
          style={{
            opacity: buttonAnim,
            transform: [
              { scale: buttonAnim.interpolate({ inputRange: [0, 1], outputRange: [0.85, 1] }) },
              { translateY: bounce.interpolate({ inputRange: [0, 1], outputRange: [0, -7] }) },
            ],
          }}
        >
          <Pressable
            onPress={checkIn}
            disabled={busy}
            accessibilityRole="button"
            accessibilityLabel={t('checkin.action')}
            style={({ pressed }) => [styles.button, pressed && styles.buttonPressed]}
          >
            <Ionicons name="hand-left" size={18} color={colors.textOnPrimary} />
            <Text style={styles.buttonLabel} numberOfLines={1}>{t('checkin.action')}</Text>
            {status.streak > 0 && (
              <View style={styles.streakPill}>
                <Text style={styles.streakPillText}>{status.streak}</Text>
              </View>
            )}
          </Pressable>
          {/* Χωρίς αυτό το κουμπί δεν λέει τι κερδίζει ο χρήστης πατώντας. */}
          <Text style={styles.buttonHint} numberOfLines={2}>{t('checkin.hint')}</Text>
        </Animated.View>
      )}

      {/* ── Γραφικό ημερών (μετά τη δήλωση) ── */}
      {done && (
        <Animated.View
          style={[
            styles.panel,
            {
              opacity: panelAnim,
              transform: [
                { scale: panelAnim.interpolate({ inputRange: [0, 1], outputRange: [0.94, 1] }) },
                { translateY: panelAnim.interpolate({ inputRange: [0, 1], outputRange: [8, 0] }) },
              ],
            },
          ]}
        >
          <View style={styles.panelRow}>
            <Animated.View
              style={{
                transform: [{ scale: burst.interpolate({ inputRange: [0, 1], outputRange: [1, 1.45] }) }],
              }}
            >
              <Ionicons
                name={justRewarded ? 'sparkles' : 'checkmark-circle'}
                size={18}
                color={justRewarded ? colors.primary : colors.success}
              />
            </Animated.View>
            <Text style={styles.panelTitle} numberOfLines={1}>
              {justRewarded ? t('checkin.rewarded') : t('checkin.doneToday')}
            </Text>
            <Text style={styles.streakCount}>{status.streak}</Text>
          </View>

          <View style={styles.dots}>
            {Array.from({ length: STREAK_TARGET }).map((_, i) => (
              <View key={i} style={[styles.dot, i < filled && styles.dotFilled]} />
            ))}
          </View>

          <Text style={styles.sub} numberOfLines={2}>
            {t('checkin.progressDone', { count: STREAK_TARGET - filled })}
          </Text>
        </Animated.View>
      )}
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    wrap: { marginBottom: spacing.sm },

    button: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.xs,
      backgroundColor: colors.primary,
      borderRadius: radius.full,
      paddingVertical: 11,
      paddingHorizontal: spacing.lg,
    },
    buttonPressed: { opacity: 0.8 },
    buttonLabel: {
      color: colors.textOnPrimary,
      fontSize: fontSize.sm,
      fontWeight: '800',
    },
    streakPill: {
      minWidth: 20,
      paddingHorizontal: 6,
      paddingVertical: 1,
      borderRadius: radius.full,
      backgroundColor: 'rgba(0,0,0,0.18)',
    },
    streakPillText: {
      color: colors.textOnPrimary,
      fontSize: fontSize.xs,
      fontWeight: '800',
      textAlign: 'center',
    },
    buttonHint: {
      color: colors.textMuted,
      fontSize: fontSize.xs,
      lineHeight: 15,
      textAlign: 'center',
      marginTop: 5,
    },

    panel: {
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.md,
      gap: spacing.xs,
    },
    panelRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
    panelTitle: {
      flex: 1,
      color: colors.text,
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
    streakCount: {
      color: colors.primary,
      fontSize: fontSize.sm,
      fontWeight: '800',
    },
    dots: { flexDirection: 'row', gap: 4 },
    dot: {
      flex: 1,
      height: 4,
      borderRadius: 2,
      backgroundColor: colors.border,
    },
    dotFilled: { backgroundColor: colors.primary },
    sub: {
      color: colors.textMuted,
      fontSize: fontSize.xs,
      lineHeight: 15,
    },
  });
}
